import type { Tour } from '../tour-types';

/**
 * The Feature flag Library, once it has flags. The empty list is a first-run state, so the tour
 * waits for the table. The status and next-step anchors are on the first row.
 */
export const flagsLibraryTour: Tour = {
  key: 'flags.library',
  version: 1,
  routes: ['/flags/library'],
  steps: [
    {
      anchor: 'flags-views',
      placement: 'bottom',
      title: 'Views',
      body: 'Narrow the list to flags rolling out, paused, fully on or off. Archived flags have a view of their own.',
    },
    {
      anchor: 'flags-search',
      placement: 'bottom',
      title: 'Search',
      body: 'Find a flag by its name or the key your code asks for.',
    },
    {
      anchor: 'flags-new',
      placement: 'left',
      requires: 'experiments:create',
      title: 'Create flag',
      body: 'Change the site with the editor, or put a flag in your own code. Nothing reaches visitors until you publish it.',
    },
    {
      anchor: 'flags-status',
      placement: 'left',
      title: 'Status',
      body: 'Rolling out means a plan is raising the rollout step by step. Paused waits for you, and Off means nobody gets On.',
    },
    {
      anchor: 'flags-next-step',
      placement: 'left',
      title: 'Next step',
      body: 'When a running plan moves to its next share.',
    },
  ],
};

/*
 * A flag's page has tabs that are pages of their own (?screen=), so each has its own tour. The
 * header and the status card are on every tab; the Rollout tab, the one a flag opens on, covers
 * them.
 */
const FLAG_ROUTES = ['/flags/:id'];

/** Rollout: what visitors get now, how it moves, and the main action in the breadcrumb bar. */
export const flagRolloutTour: Tour = {
  key: 'flags.rollout',
  version: 1,
  routes: FLAG_ROUTES,
  screen: 'rollout',
  steps: [
    {
      anchor: 'flag-status',
      title: 'Status',
      body: 'Where the flag is: draft, live or archived, the share that gets On, the plan and what happens next. A paused plan says why here.',
    },
    {
      anchor: 'flag-publish',
      placement: 'bottom',
      requires: 'experiments:start',
      title: 'Publish',
      body: 'A new flag is a draft. Publishing puts it live at its first step, or for everyone if it is an editor flag left at 0%.',
    },
    {
      anchor: 'flag-turn-off',
      placement: 'bottom',
      requires: 'experiments:start',
      title: 'Turn off',
      body: 'Everyone gets Off at once, and a running plan is paused.',
    },
    {
      anchor: 'flag-rollout',
      title: 'Rollout',
      body: 'The share of visitors who get On. Raising it only adds people, so nobody flips back and forth.',
    },
    {
      anchor: 'flag-plan',
      title: 'Rollout plan',
      body: 'Steps the rollout moves through on its own, each after its time, the guardrail or both. Pause it to change the steps.',
    },
    {
      anchor: 'flag-holdback',
      title: 'Holdback',
      body: 'A share kept Off at every step, so there is still a group to compare with at 100%.',
    },
    {
      anchor: 'flag-guardrail',
      title: 'Guardrail',
      body: 'On a step that waits on it, the last check of On against Off. No harm moves the step on, and a clear drop pauses the plan.',
    },
    {
      anchor: 'flag-schedule',
      placement: 'top',
      title: 'Schedule',
      body: 'Publish the flag or turn it off at a set time, without being there.',
    },
  ],
};

/** Brief: what the flag is for. Previews are for editor flags only. */
export const flagBriefTour: Tour = {
  key: 'flags.brief',
  version: 1,
  routes: FLAG_ROUTES,
  screen: 'brief',
  steps: [
    {
      anchor: 'brief-description',
      title: 'Description',
      body: 'What the flag does and why it ships, for anyone on your team who finds it later.',
    },
    {
      anchor: 'brief-screenshots',
      placement: 'top',
      title: 'Previews',
      body: 'Screenshots of the page with Off and with On, from the visual editor.',
    },
  ],
};

/** Targeting: who can get On at all, and who is decided before the rollout. */
export const flagTargetingTour: Tour = {
  key: 'flags.targeting',
  version: 1,
  routes: FLAG_ROUTES,
  screen: 'targeting',
  steps: [
    {
      anchor: 'flag-targeting',
      title: 'Targeting',
      body: 'The pages, devices and cookies where the flag can apply. Elsewhere it is Off. A live flag needs Publish rules after a change.',
    },
    {
      anchor: 'flag-server-rules',
      title: 'Server rules',
      body: 'For a code flag: attributes your server passes that a visitor must match to get On.',
    },
    {
      anchor: 'flag-rings',
      placement: 'top',
      title: 'Rings',
      body: 'Groups that always get one side, such as staff or a beta list, decided before the rollout.',
    },
  ],
};

/** Setup: how On is built, and the goal the guardrail judges it on. The kind decides the first card. */
export const flagSetupTour: Tour = {
  key: 'flags.setup',
  version: 1,
  routes: FLAG_ROUTES,
  screen: 'setup',
  steps: [
    {
      anchor: 'flag-editor',
      title: 'Changes',
      body: 'An editor flag applies its On changes from the visual editor. Open your site and pick the flag in the extension.',
    },
    {
      anchor: 'flag-code',
      title: 'Use it in your code',
      body: 'The SDK call for this flag, and the browser read, filled in with its key. Branch on On, and keep Off as what you do today.',
    },
    {
      anchor: 'flag-goal',
      title: 'Primary goal',
      body: 'The event the guardrail compares On and Off on. Set it before you publish.',
    },
    {
      anchor: 'flag-payloads',
      placement: 'top',
      title: 'Payloads',
      body: 'The JSON your code gets with each side, set when the flag was created.',
    },
  ],
};

/** Activity: what happened to the flag. */
export const flagActivityTour: Tour = {
  key: 'flags.activity',
  version: 1,
  routes: FLAG_ROUTES,
  screen: 'activity',
  steps: [
    {
      anchor: 'flag-activity',
      title: 'Activity',
      body: 'Every publish, rollout change and plan step, with who made it.',
    },
  ],
};

export const FLAG_TOURS: readonly Tour[] = [
  flagsLibraryTour,
  flagRolloutTour,
  flagBriefTour,
  flagTargetingTour,
  flagSetupTour,
  flagActivityTour,
];
